import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getAllBranch, upsertBdcBranch } from "../../../services";
import { BdcBranch } from "../../../types/bdc.type";
import { Branch } from "../../../types/user.type";
import BranchChip from "../Branch/BranchChip";
import { sortBranches } from "../Branch/branches";
import Tag from "../Tag";
import { branchDefaultsBody, branchDefaultsErrors, branchDefaultsInput, BranchDefaultsInput } from "./updateBody";

const DEFAULT_FIELDS: { key: keyof BranchDefaultsInput; label: string; placeholder: string; width: number; max: number }[] = [
  { key: "cn_prefix", label: "prefix CN", placeholder: "C2", width: 80, max: 2 },
  { key: "invoice_book_code", label: "รหัสเล่ม INV", placeholder: "VY", width: 100, max: 10 },
  { key: "billing_label", label: "ป้าย DC วางบิล", placeholder: "BDCBK", width: 120, max: 20 },
];

const branchOptionLabel = (branch: Branch) => `${branch.branch_code} · ${branch.branch_name}`;

/** MASTER: pick a GDP branch (GET /branches) that is not BDC yet and register it. */
function RegisterBranch({
  registered,
  onSaved,
}: Readonly<{ registered: BdcBranch[]; onSaved: (branch: BdcBranch) => void }>) {
  const [gdpBranches, setGdpBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<Branch | null>(null);
  const [testMode, setTestMode] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getAllBranch()
      .then(({ data: { data } }) => {
        if (!active) return;
        const rows: Branch[] = Array.isArray(data) ? data : data?.rows ?? [];
        setGdpBranches(rows);
      })
      .catch(() => {
        if (active) setGdpBranches([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(
    () =>
      gdpBranches
        .filter((b) => !registered.some((r) => r.company_id === String(b.company_id)))
        .sort((a, b) => a.branch_code.localeCompare(b.branch_code)),
    [gdpBranches, registered],
  );

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const {
        data: { data },
      } = await upsertBdcBranch(String(picked.company_id), { is_test_mode: testMode });
      onSaved(
        data ?? {
          company_id: String(picked.company_id),
          branch_code: picked.branch_code,
          branch_name: picked.branch_name,
          is_test_mode: testMode,
          dc_codes: [],
        },
      );
      setPicked(null);
      setTestMode(true);
    } catch {
      // interceptor shows the reason (404 = no such GDP branch)
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #E3E5EA" }}>
      <Typography variant="body2" fontWeight={600} mb={1}>
        ลงทะเบียนสาขา GDP เป็นสาขา BDC
      </Typography>
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
        <Autocomplete
          size="small"
          options={options}
          loading={loading}
          value={picked}
          onChange={(_e, value) => setPicked(value)}
          getOptionLabel={branchOptionLabel}
          isOptionEqualToValue={(a, b) => String(a.company_id) === String(b.company_id)}
          noOptionsText="ไม่มีสาขาที่ยังไม่ได้ลงทะเบียน"
          loadingText="กำลังโหลดสาขา…"
          sx={{ minWidth: 320 }}
          renderInput={(params) => <TextField {...params} placeholder="เลือกสาขา GDP" />}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />}
          label="เริ่มในโหมดทดสอบ"
        />
        <Button variant="contained" disabled={!picked} loading={saving} onClick={save}>
          ลงทะเบียน
        </Button>
      </Stack>
    </Box>
  );
}

function BranchRow({
  branch,
  branches,
  canWrite,
  onSaved,
}: Readonly<{
  branch: BdcBranch;
  branches: BdcBranch[];
  canWrite: boolean;
  onSaved: (branch: BdcBranch) => void;
}>) {
  const [saving, setSaving] = useState(false);
  const [input, setInput] = useState<BranchDefaultsInput>(() => branchDefaultsInput(branch));
  const [savingDefaults, setSavingDefaults] = useState(false);
  useEffect(() => {
    setInput(branchDefaultsInput(branch));
  }, [branch]);
  const errors = branchDefaultsErrors(input);
  const hasErrors = Object.keys(errors).length > 0;
  const defaultsBody = hasErrors ? null : branchDefaultsBody(branch, input);

  const saveDefaults = async () => {
    if (!defaultsBody) return;
    setSavingDefaults(true);
    try {
      const {
        data: { data },
      } = await upsertBdcBranch(branch.company_id, defaultsBody);
      onSaved(data ?? { ...branch, ...defaultsBody });
    } catch {
      // interceptor shows the reason (409 = prefix CN ซ้ำกับสาขาอื่น)
    } finally {
      setSavingDefaults(false);
    }
  };

  const toggle = async (next: boolean) => {
    setSaving(true);
    try {
      const {
        data: { data },
      } = await upsertBdcBranch(branch.company_id, { is_test_mode: next });
      onSaved(data ?? { ...branch, is_test_mode: next });
    } catch {
      // interceptor shows the reason
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td>
        <BranchChip branches={branches} companyId={branch.company_id} branchCode={branch.branch_code} />
      </td>
      <td>{branch.branch_name ?? "-"}</td>
      <td>
        {branch.dc_codes.length > 0 ? (
          branch.dc_codes.join(", ")
        ) : (
          <span className="sub">ยังไม่มี DC ผูก</span>
        )}
      </td>
      <td>
        {canWrite ? (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={branch.is_test_mode}
                disabled={saving}
                onChange={(e) => toggle(e.target.checked)}
                slotProps={{ input: { "aria-label": `โหมดทดสอบ ${branch.branch_code ?? branch.company_id}` } }}
              />
            }
            label={branch.is_test_mode ? "ทดสอบ" : "ใช้งานจริง"}
          />
        ) : branch.is_test_mode ? (
          <Tag tone="warn">ทดสอบ</Tag>
        ) : (
          <Tag tone="ok">ใช้งานจริง</Tag>
        )}
      </td>
      {DEFAULT_FIELDS.map((field) => (
        <td key={field.key} className={canWrite ? undefined : "mono"}>
          {canWrite ? (
            <TextField
              size="small"
              value={input[field.key]}
              placeholder={field.placeholder}
              onChange={(e) => setInput((current) => ({ ...current, [field.key]: e.target.value }))}
              error={Boolean(errors[field.key])}
              helperText={errors[field.key]}
              sx={{ width: field.width }}
              slotProps={{
                htmlInput: { maxLength: field.max, "aria-label": `${field.label} ${branch.branch_code ?? branch.company_id}` },
              }}
            />
          ) : (
            branch[field.key] || <span className="sub">ยังไม่ตั้ง</span>
          )}
        </td>
      ))}
      {canWrite && (
        <td>
          <Button size="small" variant="contained" disabled={!defaultsBody} loading={savingDefaults} onClick={saveDefaults}>
            บันทึก
          </Button>
        </td>
      )}
    </tr>
  );
}

/**
 * สาขา BDC — the GDP branches registered as BDC branches, the DCs each delivers and their test
 * mode. MASTER registers a branch and flips test mode; everyone else reads.
 */
export default function BranchTab({
  branches,
  loading,
  canWrite,
  onSaved,
}: Readonly<{
  branches: BdcBranch[];
  loading: boolean;
  canWrite: boolean;
  onSaved: (branch: BdcBranch) => void;
}>) {
  const sorted = sortBranches(branches);
  return (
    <Box>
      <Typography className="sub" sx={{ px: 2, py: 1.25 }}>
        สาขา GDP ที่ลงทะเบียนเป็นสาขา BDC · DC ผูกกับสาขาที่แท็บ DC · โหมดทดสอบ: ตอนนี้แสดงแถบเตือนบนหน้าจอ
        (ผลต่อเลข INV / ไฟล์ 7-11 มากับเฟสออก INV) · prefix CN / รหัสเล่ม INV / ป้าย DC ใช้กับ CN และใบวางบิล —
        ตั้งแล้วระบบสร้างชุดเลขให้ (ดูที่แท็บเลขเอกสาร) · ออกใบวางบิลไม่ได้ถ้าสาขาในใบยังไม่ตั้งครบ
        {!canWrite && " · ลงทะเบียนสาขา เปลี่ยนโหมดทดสอบ และค่าตั้งต้นได้เฉพาะ MASTER"}
      </Typography>
      {loading && <LinearProgress />}
      <table className="bdc-t">
        <thead>
          <tr>
            <th style={{ width: 110 }}>สาขา</th>
            <th>ชื่อ</th>
            <th>DC ที่จัดส่ง</th>
            <th style={{ width: 180 }}>โหมดทดสอบ</th>
            {DEFAULT_FIELDS.map((field) => (
              <th key={field.key}>{field.label}</th>
            ))}
            {canWrite && <th />}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && !loading ? (
            <tr>
              <td colSpan={8} className="c sub">
                ยังไม่มีสาขาที่ลงทะเบียนเป็นสาขา BDC{canWrite ? " — ลงทะเบียนด้านล่าง" : " — ให้ MASTER ลงทะเบียน"}
              </td>
            </tr>
          ) : (
            sorted.map((branch) => (
              <BranchRow
                key={branch.company_id}
                branch={branch}
                branches={sorted}
                canWrite={canWrite}
                onSaved={onSaved}
              />
            ))
          )}
        </tbody>
      </table>
      {canWrite && <RegisterBranch registered={branches} onSaved={onSaved} />}
    </Box>
  );
}
